import React, { useState } from "react";
import "./RatingModal.css";
import { Modal, Input, Button, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWindowClose } from "@fortawesome/free-solid-svg-icons";
import { useMutation } from "@apollo/client";
import { useTranslation, Trans } from "react-i18next";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

// graphql
import RATE_CLASS from "graphql/mutations/RateClass";
import GET_CLASS_DETAILS from "graphql/queries/GetClassDetails";

// types
import { Rating, Lesson } from "types";

const { TextArea } = Input;

type ratingModalPropType = {
  visible: boolean;
  onCloseModal: () => void;
  rating?: Rating;
  state: Lesson;
};

const defaultRating = {
  teacher: {
    rating: 0,
    comment: "",
  },
  material: {
    rating: 0,
    comment: "",
  },
};

function RatingModal(props: ratingModalPropType) {
  const { t } = useTranslation("rating");

  const { visible, onCloseModal, state, rating } = props;
  const ratingNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const [currentRating, setRating] = useState(rating || defaultRating);

  const [rateClass] = useMutation(RATE_CLASS, {
    refetchQueries: [
      {
        query: GET_CLASS_DETAILS,
        variables: { id: state.id },
      },
    ],
    onCompleted: () => {
      message.success(t("Rating created successfully!"));
    },
    onError: (error) => {
      message.error(error.graphQLErrors[0].message);
    },
  });

  const onSubmitRating = () => {
    const attributes = {
      classId: state.id,
      teacherId: state.teacher?.id,
      rating: currentRating,
    };

    rateClass({
      variables: {
        input: {
          attributes,
        },
      },
    });
    onCloseModal();
  };

  const handleChangeRatingAndComment = (ev: any) => {
    // only allows changing the rating/comment if there isn't
    // already a rating for the class
    if (!rating) {
      if (ev.target.id === "teacher-rating") {
        setRating({
          ...currentRating,
          teacher: {
            ...currentRating.teacher,
            rating: ev.target.tabIndex,
          },
        });
      } else if (ev.target.id === "material-rating") {
        setRating({
          ...currentRating,
          material: {
            ...currentRating.material,
            rating: ev.target.tabIndex,
          },
        });
      } else if (ev.target.id === "teacher-comment") {
        setRating({
          ...currentRating,
          teacher: {
            ...currentRating.teacher,
            comment: ev.target.value,
          },
        });
      } else if (ev.target.id === "material-comment") {
        setRating({
          ...currentRating,
          material: {
            ...currentRating.material,
            comment: ev.target.value,
          },
        });
      }
    }
  };

  // Use optional chaining here to bypass typescript
  // Only render RatingModal when there's a teacher, so teacher cannot be null
  const { givenName, familyName } = state.teacher || {};
  const topicTitle = state.topic?.title;

  return (
    <Modal
      className="modal"
      title={t("RATE MY CLASS")}
      centered
      closeIcon={
        <FontAwesomeIcon
          icon={faWindowClose as IconProp}
          className="question-icon"
          size="2x"
        />
      }
      visible={visible}
      footer={null}
      onCancel={onCloseModal}
    >
      <span>
        <Trans t={t}>
          Rate{" "}
          <strong>
            {{ givenName }} {{ familyName }}
          </strong>{" "}
          for this class
        </Trans>
      </span>
      <div className="rating-number-row">
        {ratingNumbers.map((ratingNumber: number) => (
          <div
            id="teacher-rating"
            className={`rating-number ${
              currentRating.teacher.rating === ratingNumber ? "chosen" : ""
            }`}
            data-rated={rating !== null}
            key={ratingNumber}
            tabIndex={ratingNumber}
            onFocus={handleChangeRatingAndComment}
          >
            {ratingNumber}
          </div>
        ))}
      </div>
      <div className="rating-number-text">
        <span>{t("Very Bad")}</span>
        <span>{t("Fantastic")}</span>
      </div>
      <TextArea
        id="teacher-comment"
        className="text-area"
        autoSize={{ minRows: 4, maxRows: 4 }}
        placeholder={t(
          "Comments about the teacher. Constructive criticism is welcome. Individual rating scores are not sent to teachers."
        )}
        onChange={handleChangeRatingAndComment}
        value={currentRating.teacher.comment}
        disabled={rating !== null}
      />
      <span>
        <Trans t={t}>
          Rate the teaching PDF and audio file for{" "}
          <strong>{{ topicTitle }}</strong>
        </Trans>
      </span>
      <div className="rating-number-row">
        {ratingNumbers.map((ratingNumber: number) => (
          <div
            id="material-rating"
            className={`rating-number ${
              currentRating.material.rating === ratingNumber ? "chosen" : ""
            }`}
            data-rated={rating !== null}
            key={ratingNumber}
            tabIndex={ratingNumber}
            onFocus={handleChangeRatingAndComment}
          >
            {ratingNumber}
          </div>
        ))}
      </div>
      <div className="rating-number-text">
        <span>{t("Very Bad")}</span>
        <span>{t("Fantastic")}</span>
      </div>
      <TextArea
        id="material-comment"
        className="text-area"
        autoSize={{ minRows: 4, maxRows: 4 }}
        placeholder={t("Comments about the learning materials")}
        onChange={handleChangeRatingAndComment}
        value={currentRating.material.comment}
        disabled={rating !== null}
      />
      {rating ? (
        <div>{t("I already rated this class")}.</div>
      ) : (
        <div className="modal-button-container">
          <Button
            className="modal-button"
            type="primary"
            onClick={onSubmitRating}
          >
            {t("SUBMIT RATING")}
          </Button>
        </div>
      )}
    </Modal>
  );
}

export default RatingModal;
